import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Lottie from 'lottie-react';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [attendanceData, setAttendanceData] = useState(null);
  const [rawAttendanceData, setRawAttendanceData] = useState(null); // Store raw data for Travel leave logic
  const [workLocations, setWorkLocations] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [fetchingLocation, setFetchingLocation] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [confirmAtOffice, setConfirmAtOffice] = useState(false);
  const [ipValidationStatus, setIpValidationStatus] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loadingLeaveBalance, setLoadingLeaveBalance] = useState(false);
  const [birthdayEmployees, setBirthdayEmployees] = useState([]);
  const [loadingBirthdayEmployees, setLoadingBirthdayEmployees] = useState(false);
  const [showBirthdayDropdown, setShowBirthdayDropdown] = useState(false);
  const [confettiData, setConfettiData] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [formData, setFormData] = useState({
    locationId: '',
    locationName: '',
    notes: ''
  });

  useEffect(() => {
    fetchTodayAttendance();
    fetchLeaveBalance();
    fetchBirthdayEmployees();
    loadConfettiAnimation();
    // Detect if user is on desktop (no GPS hardware)
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    setIsDesktop(!isMobile);
    
    // Automatically fetch current location when component loads
    getUserLocation(false).then(location => {
      console.log('Auto-fetched location on mount:', location);
    }).catch(error => {
      console.warn('Auto-fetch location failed:', error);
    });
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load confetti animation JSON
  const loadConfettiAnimation = async () => {
    try {
      const response = await fetch('/assets/json/Confetti.json');
      if (!response.ok) {
        console.error('Failed to load confetti animation:', response.status, response.statusText);
        return null;
      }
      const data = await response.json();
      setConfettiData(data);
      console.log('Confetti animation loaded successfully');
      return data;
    } catch (error) {
      console.error('Error loading confetti animation:', error);
      return null;
    }
  };

  // Check if confetti should be shown today
  const shouldShowConfettiToday = () => {
    const today = new Date().toDateString(); // Format: "Mon Dec 27 2025"
    const lastConfettiDate = localStorage.getItem('confettiShownDate');
    
    // If no date stored or date is different (new day), show confetti
    if (!lastConfettiDate || lastConfettiDate !== today) {
      return true;
    }
    
    // If date matches today, don't show again
    return false;
  };

  // Store confetti shown date
  const markConfettiAsShown = () => {
    const today = new Date().toDateString();
    localStorage.setItem('confettiShownDate', today);
  };

  // Trigger confetti animation
  const triggerConfetti = (data = null) => {
    // Use provided data or state data
    const animationData = data || confettiData;
    
    console.log('triggerConfetti called:', { 
      hasAnimationData: !!animationData, 
      shouldShow: shouldShowConfettiToday(),
      providedData: !!data,
      stateData: !!confettiData
    });
    
    if (animationData && shouldShowConfettiToday()) {
      // Set the data in state if it was provided
      if (data && !confettiData) {
        setConfettiData(data);
      }
      console.log('Triggering confetti animation');
      setShowConfetti(true);
      // Mark as shown for today
      markConfettiAsShown();
      // Hide confetti after animation completes (typically 3-5 seconds)
      setTimeout(() => {
        setShowConfetti(false);
      }, 5000);
    } else {
      console.log('Confetti not triggered:', {
        hasData: !!animationData,
        shouldShow: shouldShowConfettiToday()
      });
    }
  };

  // Fetch work locations when modal opens
  useEffect(() => {
    if (showCheckInModal) {
      fetchWorkLocations(true);
    }
  }, [showCheckInModal]);

  // Close birthday dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showBirthdayDropdown && !event.target.closest('.dashboard-birthday-dropdown-container')) {
        setShowBirthdayDropdown(false);
      }
    };
    if (showBirthdayDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showBirthdayDropdown]);

  // Trigger confetti when both confettiData and birthdayEmployees are available
  useEffect(() => {
    if (confettiData && birthdayEmployees && birthdayEmployees.length > 0 && shouldShowConfettiToday() && !showConfetti) {
      console.log('useEffect triggering confetti:', {
        hasConfettiData: !!confettiData,
        birthdayCount: birthdayEmployees.length,
        shouldShow: shouldShowConfettiToday(),
        alreadyShowing: showConfetti
      });
      setShowConfetti(true);
      markConfettiAsShown();
      setTimeout(() => {
        setShowConfetti(false);
      }, 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confettiData, birthdayEmployees]);

  const fetchWorkLocations = async (showLoading = false) => {
    if (!API_URL) return;

    const userId = localStorage.getItem('id');
    if (!userId) return;

    try {
      const payload = {
        action: 'get-work-locations',
        status: 'active',
        page: 1,
        limit: 10,
        currentUserId: userId
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        const locations = result.data || [];
        setWorkLocations(locations);
        
        // Debug: Check if IP ranges are configured
        console.log('=== Work Locations Data ===');
        locations.forEach((location, index) => {
          const hasIPRanges = !!location.ipRanges;
          const ipRangesCount = location.ipRanges ? location.ipRanges.length : 0;
          const isEmpty = hasIPRanges && ipRangesCount === 0;
          
          console.log(`Location ${index + 1}:`, {
            id: location.id,
            name: location.locationName,
            hasIPRanges: hasIPRanges,
            ipRanges: isEmpty ? 'EMPTY ARRAY - NEEDS IP RANGES' : (location.ipRanges || 'NOT CONFIGURED'),
            ipRangesCount: ipRangesCount
          });
          
          if (isEmpty) {
            console.warn(`⚠️ ${location.locationName}: ipRanges field exists but is EMPTY. Please add IP ranges to database.`);
            console.warn(`   Location ID: ${location.id}`);
            console.warn(`   Example IP ranges: ["192.168.1.0/24", "10.0.0.0/8"]`);
          }
        });
        console.log('==========================');
      }
    } catch (error) {
      console.error('Error fetching work locations:', error);
      toast.error('Failed to fetch work locations');
    }
  };

  // Get user's IP address
  const getUserIPAddress = async () => {
    try {
      const services = [
        'https://api.ipify.org?format=json',
        'https://api.ip.sb/ip',
        'https://api.ipify.org'
      ];

      for (const service of services) {
        try {
          const response = await fetch(service, { timeout: 3000 });
          if (service.includes('json')) {
            const data = await response.json();
            return data.ip || data.query || null;
          } else {
            const text = await response.text();
            return text.trim() || null;
          }
        } catch (err) {
          console.warn(`Failed to get IP from ${service}:`, err);
          continue;
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting IP address:', error);
      return null;
    }
  };

  // Check if IP address matches office IP range
  const isIPInOfficeRange = (userIP, officeIPRanges) => {
    if (!userIP || !officeIPRanges || officeIPRanges.length === 0) {
      return false;
    }

    // Convert IP to number for range checking
    const ipToNumber = (ip) => {
      return ip.split('.').reduce((acc, octet) => (acc << 8) + parseInt(octet, 10), 0) >>> 0;
    };

    // Check if IP matches any range
    for (const range of officeIPRanges) {
      if (!range) continue;
      
      // Handle CIDR notation (e.g., "192.168.1.0/24")
      if (range.includes('/')) {
        const [network, prefixLength] = range.split('/');
        const networkNum = ipToNumber(network);
        const mask = (0xFFFFFFFF << (32 - parseInt(prefixLength, 10))) >>> 0;
        const userIPNum = ipToNumber(userIP);
        
        if ((userIPNum & mask) === (networkNum & mask)) {
          return true;
        }
      } 
      // Handle exact IP match
      else if (range === userIP) {
        return true;
      }
      // Handle IP range (e.g., "192.168.1.1-192.168.1.100")
      else if (range.includes('-')) {
        const [startIP, endIP] = range.split('-').map(ip => ip.trim());
        const startNum = ipToNumber(startIP);
        const endNum = ipToNumber(endIP);
        const userIPNum = ipToNumber(userIP);
        
        if (userIPNum >= startNum && userIPNum <= endNum) {
          return true;
        }
      }
    }
    
    return false;
  };

  // Get more accurate location using IP geolocation API as fallback
  const getLocationFromIP = async () => {
    try {
      // Try multiple free IP geolocation services for better accuracy
      const services = [
        'https://ipapi.co/json/',
        'https://ip-api.com/json/',
        'https://geolocation-db.com/json/'
      ];

      for (const service of services) {
        try {
          const response = await fetch(service, { timeout: 5000 });
          const data = await response.json();
          
          if (data.latitude && data.longitude) {
            return {
              latitude: parseFloat(data.latitude),
              longitude: parseFloat(data.longitude),
              accuracy: 1000, // IP-based location typically 1-5km accuracy
              source: 'ip-geolocation',
              ip: data.ip || data.query || null
            };
          }
        } catch (err) {
          console.warn(`Failed to get location from ${service}:`, err);
          continue;
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting IP geolocation:', error);
      return null;
    }
  };

  // Get user's current location with maximum accuracy using HTML5 Geolocation API
  const getUserLocation = async (showLoading = false) => {
    if (!navigator.geolocation) {
      // Try IP geolocation as fallback
      if (showLoading) {
        setFetchingLocation(true);
      }
      const ipLocation = await getLocationFromIP();
      if (ipLocation) {
        if (showLoading) {
          setFetchingLocation(false);
        }
        return ipLocation;
      }
      return Promise.reject('Geolocation not supported');
    }

    if (showLoading) {
      setFetchingLocation(true);
    }

    return new Promise((resolve, reject) => {
      let locations = []; // Store multiple location readings
      let bestLocation = null;
      let attempts = 0;
      const maxAttempts = isDesktop ? 50 : 20; // More attempts for desktop Wi-Fi positioning
      let watchId = null;
      let timeoutId = null;

      // Cleanup function
      const cleanup = () => {
        if (watchId !== null) {
          navigator.geolocation.clearWatch(watchId);
          watchId = null;
        }
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      };

      // Resolve with best location
      const resolveWithLocation = (location, isBest = true) => {
        cleanup();
        const finalLocation = isBest ? bestLocation : location;
        if (finalLocation) {
          console.log('Final location:', {
            latitude: finalLocation.latitude,
            longitude: finalLocation.longitude,
            accuracy: finalLocation.accuracy + ' meters'
          });
          if (showLoading) {
            setFetchingLocation(false);
          }
          resolve(finalLocation);
        } else {
          if (showLoading) {
            setFetchingLocation(false);
          }
          reject(new Error('No location received'));
        }
      };

      // High accuracy options for HTML5 Geolocation API
      // For desktop: enableHighAccuracy uses Wi-Fi positioning which is more accurate than IP-based
      // For mobile: enableHighAccuracy uses GPS
      const highAccuracyOptions = {
        enableHighAccuracy: true,  // Use Wi-Fi positioning on desktop, GPS on mobile
        timeout: isDesktop ? 180000 : 60000,  // Desktop: 180 seconds (3 min) for Wi-Fi positioning to improve, Mobile: 60 seconds for GPS
        maximumAge: 0  // Always get fresh location - don't use cache for better accuracy
      };

      // Method 1: Try getCurrentPosition first for immediate result
      // On desktop, this will use Wi-Fi positioning which is more accurate than IP-based
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            speed: position.coords.speed
          };

          console.log('Initial location from getCurrentPosition:', {
            latitude: location.latitude,
            longitude: location.longitude,
            accuracy: location.accuracy + ' meters',
            altitude: location.altitude,
            altitudeAccuracy: location.altitudeAccuracy,
            source: isDesktop ? 'Wi-Fi positioning' : 'GPS'
          });

          bestLocation = location;
          setUserLocation(location);

          // On desktop with Wi-Fi, good accuracy is typically 20-100m
          // On mobile with GPS, excellent accuracy is ≤10m
          const excellentThreshold = isDesktop ? 50 : 10;
          
          if (location.accuracy <= excellentThreshold) {
            console.log(`Excellent ${isDesktop ? 'Wi-Fi' : 'GPS'} accuracy achieved:`, location.accuracy, 'meters');
            resolveWithLocation(location, false);
            return;
          }
        },
        (error) => {
          console.warn('getCurrentPosition failed, will use watchPosition:', error.message);
        },
        highAccuracyOptions
      );

      // Method 2: Use watchPosition for continuous updates and better accuracy
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          attempts++;
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            speed: position.coords.speed
          };

          console.log(`Location attempt ${attempts}:`, {
            latitude: location.latitude,
            longitude: location.longitude,
            accuracy: location.accuracy + ' meters',
            altitude: location.altitude,
            altitudeAccuracy: location.altitudeAccuracy
          });

          // Store all location readings for averaging (especially useful for desktop)
          locations.push(location);
          
          // Always update to the best location found (lowest accuracy = most accurate)
          if (!bestLocation || location.accuracy < bestLocation.accuracy) {
            bestLocation = location;
            setUserLocation(location);
            console.log(`New best location found with accuracy: ${location.accuracy.toFixed(2)} meters (${isDesktop ? 'Wi-Fi' : 'GPS'})`);
          }
          
          // For desktop, try to average multiple readings for better accuracy
          if (isDesktop && locations.length >= 5) {
            // Calculate average of last 5-10 readings to reduce network-based location errors
            const readingsToAverage = Math.min(locations.length, 10);
            const recentLocations = locations.slice(-readingsToAverage);
            const avgLat = recentLocations.reduce((sum, loc) => sum + loc.latitude, 0) / readingsToAverage;
            const avgLng = recentLocations.reduce((sum, loc) => sum + loc.longitude, 0) / readingsToAverage;
            const avgAccuracy = recentLocations.reduce((sum, loc) => sum + loc.accuracy, 0) / readingsToAverage;
            
            const averagedLocation = {
              latitude: avgLat,
              longitude: avgLng,
              accuracy: avgAccuracy,
              source: 'averaged',
              altitude: location.altitude,
              altitudeAccuracy: location.altitudeAccuracy,
              heading: location.heading,
              speed: location.speed
            };
            
            console.log(`Averaged location from ${readingsToAverage} readings:`, {
              latitude: avgLat,
              longitude: avgLng,
              accuracy: avgAccuracy + ' meters'
            });
            
            // Use averaged location if it's better or similar accuracy
            if (avgAccuracy <= bestLocation.accuracy * 1.2) {
              bestLocation = averagedLocation;
              setUserLocation(averagedLocation);
            }
          }

          // For desktop Wi-Fi positioning, be more patient and wait for better accuracy
          if (isDesktop) {
            // Excellent Wi-Fi accuracy (≤20 meters) - resolve immediately
            if (location.accuracy <= 20) {
              console.log('Excellent Wi-Fi accuracy achieved:', location.accuracy.toFixed(2), 'meters');
              resolveWithLocation(location, false);
              return;
            }

            // Very good Wi-Fi accuracy (≤50 meters) - resolve after 5 attempts
            if (location.accuracy <= 50 && attempts >= 5) {
              console.log('Very good Wi-Fi accuracy achieved:', location.accuracy.toFixed(2), 'meters');
              resolveWithLocation(location, false);
              return;
            }

            // Good Wi-Fi accuracy (≤100 meters) - resolve after 10 attempts
            if (location.accuracy <= 100 && attempts >= 10) {
              console.log('Good Wi-Fi accuracy achieved:', location.accuracy.toFixed(2), 'meters');
              resolveWithLocation(location, false);
              return;
            }

            // Acceptable Wi-Fi accuracy (≤200 meters) - resolve after 20 attempts
            if (location.accuracy <= 200 && attempts >= 20) {
              console.log('Acceptable Wi-Fi accuracy achieved:', location.accuracy.toFixed(2), 'meters');
              resolveWithLocation(location, false);
              return;
            }

            // Poor accuracy but improving - keep trying if we haven't reached max attempts
            if (location.accuracy > 2000 && attempts < maxAttempts) {
              console.log(`Poor Wi-Fi accuracy (${location.accuracy.toFixed(2)}m) - continuing to collect more samples... (attempt ${attempts}/${maxAttempts})`);
              return; // Continue collecting
            }

            // After max attempts, use the best location we got
            if (attempts >= maxAttempts) {
              if (bestLocation.accuracy > 2000) {
                console.warn(`Max attempts reached but accuracy is still poor (${bestLocation.accuracy.toFixed(2)}m). This may be IP-based location.`);
              } else {
                console.log(`Max attempts reached. Using best location with accuracy: ${bestLocation.accuracy.toFixed(2)} meters`);
              }
              resolveWithLocation(bestLocation, true);
            }
          } else {
            // Mobile GPS logic (unchanged)
            // Excellent accuracy (≤10 meters) - resolve immediately
            if (location.accuracy <= 10) {
              console.log('Excellent GPS accuracy achieved:', location.accuracy.toFixed(2), 'meters');
              resolveWithLocation(location, false);
              return;
            }

            // Very good accuracy (≤20 meters) - resolve after 2 attempts
            if (location.accuracy <= 20 && attempts >= 2) {
              console.log('Very good GPS accuracy achieved:', location.accuracy.toFixed(2), 'meters');
              resolveWithLocation(location, false);
              return;
            }

            // Good accuracy (≤50 meters) - resolve after 5 attempts
            if (location.accuracy <= 50 && attempts >= 5) {
              console.log('Good GPS accuracy achieved:', location.accuracy.toFixed(2), 'meters');
              resolveWithLocation(location, false);
              return;
            }

            // Acceptable accuracy (≤100 meters) - resolve after 10 attempts
            if (location.accuracy <= 100 && attempts >= 10) {
              console.log('Acceptable GPS accuracy achieved:', location.accuracy.toFixed(2), 'meters');
              resolveWithLocation(location, false);
              return;
            }

            // After max attempts, use the best location we got
            if (attempts >= maxAttempts) {
              console.log(`Max attempts reached. Using best location with accuracy: ${bestLocation.accuracy.toFixed(2)} meters`);
              resolveWithLocation(bestLocation, true);
            }
          }
        },
        (error) => {
          cleanup();

          let errorMessage = 'Unable to get your location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied by user. Please allow location access in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable. Please check your GPS settings and ensure you have a clear view of the sky.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again. Make sure GPS is enabled and you have a clear view of the sky.';
              break;
            default:
              errorMessage = 'An unknown error occurred while getting your location.';
              break;
          }
          if (showLoading) {
            setFetchingLocation(false);
          }
          reject(new Error(errorMessage));
        },
        highAccuracyOptions
      );

      // Extended timeout - longer for desktop (Wi-Fi positioning needs more time to improve)
      // Desktop: 180 seconds (3 min) for Wi-Fi positioning to get better accuracy, Mobile: 90 seconds for GPS
      const timeoutDuration = isDesktop ? 180000 : 90000;
      timeoutId = setTimeout(async () => {
        if (bestLocation) {
          // On desktop, Wi-Fi positioning should give better accuracy than IP
          // Only try IP geolocation if Wi-Fi accuracy is very poor (> 2000m)
          // If accuracy is still very poor (>2000m), it's likely IP-based, not Wi-Fi positioning
          if (isDesktop && bestLocation.accuracy > 2000) {
            console.warn(`Poor location accuracy (${bestLocation.accuracy.toFixed(2)}m) - likely using IP geolocation instead of Wi-Fi positioning.`);
            console.warn('This may happen if:');
            console.warn('1. Browser cannot access Wi-Fi positioning data');
            console.warn('2. Location services are disabled in browser/system');
            console.warn('3. Wi-Fi network is not in location databases');
            console.warn('Trying IP geolocation as fallback...');
            try {
              const ipLocation = await getLocationFromIP();
              if (ipLocation && ipLocation.accuracy < bestLocation.accuracy) {
                console.log('IP geolocation result:', ipLocation);
                cleanup();
                if (showLoading) {
                  setFetchingLocation(false);
                }
                resolve(ipLocation);
                return;
              }
            } catch (err) {
              console.warn('IP geolocation fallback failed:', err);
            }
          }
          console.log(`Timeout reached. Using best ${isDesktop ? 'Wi-Fi' : 'GPS'} location with accuracy: ${bestLocation.accuracy.toFixed(2)} meters`);
          resolveWithLocation(bestLocation, true);
        } else {
          // Try IP geolocation as last resort only on desktop
          if (isDesktop) {
            console.log('No Wi-Fi location received, trying IP geolocation...');
            try {
              const ipLocation = await getLocationFromIP();
              if (ipLocation) {
                cleanup();
                if (showLoading) {
                  setFetchingLocation(false);
                }
                resolve(ipLocation);
                return;
              }
            } catch (err) {
              console.warn('IP geolocation fallback failed:', err);
            }
          }
          cleanup();
          if (showLoading) {
            setFetchingLocation(false);
          }
          const errorMsg = isDesktop 
            ? 'Location timeout - Wi-Fi positioning unavailable. Please ensure Wi-Fi is enabled and try again, or use manual coordinate entry.'
            : 'Location timeout - no location received. Please ensure GPS is enabled and try again.';
          reject(new Error(errorMsg));
        }
      }, timeoutDuration);
    });
  };

  // Calculate distance between two coordinates using Haversine formula (in meters)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in meters
    return distance;
  };

  // Get allowed radius based on location name (50m for Head Office, 500m for others)
  const getAllowedRadius = (locationName) => {
    if (!locationName) return 50;
    const nameLower = locationName.toLowerCase();
    if (nameLower.includes('head office') || nameLower.includes('headoffice')) {
      return 50; // 50 meters for Head Office - requires accurate Wi-Fi positioning or GPS
    }
    return 500; // 500 meters for others (like Warehouse)
  };

  const formatDate = (date) => {
    if (!date) return '-';

    // If the API already returns "DD-MM-YYYY" or "DD/MM/YYYY", keep it consistent
    if (typeof date === 'string') {
      const normalized = date.replace(/\//g, '-'); // allow both separators
      const parts = normalized.split('-');
      if (parts.length === 3) {
        const [d, m, y] = parts;
        if (d && m && y) {
          return `${String(d).padStart(2, '0')}-${String(m).padStart(2, '0')}-${y}`;
        }
      }
    }

    // Fallback: parse as Date object
    const d = new Date(date);
    if (isNaN(d)) return String(date); // if parsing fails, show raw value to avoid NaN
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDateTime = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const formattedHours = String(hours).padStart(2, '0');
    return `${day}/${month}/${year} ${formattedHours}:${minutes} ${ampm}`;
  };

  const getCurrentDate = () => {
    return formatDate(new Date());
  };

  const getCurrentDateTime = () => {
    return formatDateTime(new Date());
  };

  const fetchTodayAttendance = async () => {
    const userId = localStorage.getItem('id');
    if (!userId || !API_URL) return;

    setLoading(true);
    try {
      const todayDate = getCurrentDate(); // Format: DD-MM-YYYY
      const payload = {
        action: 'get-user-attendance',
        userId: userId,
        date: todayDate
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success && result?.data && result.data.length > 0) {
        // Get the first attendance record for today
        const attendance = result.data[0];
        // Store raw data for Travel leave logic
        setRawAttendanceData(attendance);
        setAttendanceData({
          date: attendance.date,
          checkInTime: formatTimeForDisplay(attendance.checkInTime, attendance.date),
          checkOutTime: attendance.checkOutTime ? formatTimeForDisplay(attendance.checkOutTime, attendance.date) : '',
          totalHours: attendance.totalHours || '',
          status: attendance.status || 'present',
          location: attendance.location || '',
          notes: attendance.notes || '',
          leaveType: attendance.leaveType || null,
          workType: attendance.workType || null
        });
      } else {
        // No attendance record for today
        setAttendanceData(null);
        setRawAttendanceData(null);
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
      // Don't show error toast on initial load, only log it
    } finally {
      setLoading(false);
    }
  };

  // Format time from "14:52:00" to "02/12/2025 02:52 pm" format with date
  const formatTimeForDisplay = (timeString, dateString) => {
    if (!timeString) return '';
    
    try {
      // Parse time string (format: "HH:MM:SS" or "HH:MM")
      const timeParts = timeString.split(':');
      const hours = parseInt(timeParts[0], 10);
      const minutes = timeParts[1] || '00';
      
      // Convert to 12-hour format
      const ampm = hours >= 12 ? 'pm' : 'am';
      let displayHours = hours % 12;
      displayHours = displayHours ? displayHours : 12; // the hour '0' should be '12'
      const formattedHours = String(displayHours).padStart(2, '0');
      const formattedMinutes = String(minutes).padStart(2, '0');
      
      // Combine with date (format: "DD-MM-YYYY")
      // Return format: "DD/MM/YYYY HH:MM AM/PM"
      if (dateString) {
        // Convert "02-12-2025" to "02/12/2025"
        const formattedDate = dateString.split('-').join('/');
        return `${formattedDate} ${formattedHours}:${formattedMinutes} ${ampm}`;
      } else {
        return `${formattedHours}:${formattedMinutes} ${ampm}`;
      }
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString; // Return original if parsing fails
    }
  };

  const fetchLeaveBalance = async () => {
    if (!API_URL) return;

    const employeeId = localStorage.getItem('id');
    if (!employeeId) return;

    setLoadingLeaveBalance(true);
    try {
      const payload = {
        action: 'get-leave-balance',
        employeeId: employeeId,
        year: new Date().getFullYear()
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        setLeaveBalance(result.balance);
      }
    } catch (error) {
      console.error('Error fetching leave balance:', error);
    } finally {
      setLoadingLeaveBalance(false);
    }
  };

  const fetchBirthdayEmployees = async () => {
    if (!API_URL) return;

    setLoadingBirthdayEmployees(true);
    try {
      const payload = {
        action: 'get-today-birthday-employees'
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success && result.employees) {
        setBirthdayEmployees(result.employees);
        
        // Trigger confetti if count > 0 and not shown today
        if (result.count && result.count > 0) {
          // Check if confetti should be shown today
          if (shouldShowConfettiToday()) {
            // Wait for confetti data to load if not already loaded
            if (confettiData) {
              triggerConfetti();
            } else {
              // Load confetti and then trigger with the loaded data
              loadConfettiAnimation().then((data) => {
                if (data) {
                  triggerConfetti(data);
                }
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching birthday employees:', error);
    } finally {
      setLoadingBirthdayEmployees(false);
    }
  };



  const handleCheckIn = async () => {
    // Prevent double-click / duplicate submissions
    if (checkingIn) {
      console.log('Check-in already in progress, ignoring duplicate click');
      return;
    }

    const userId = localStorage.getItem('id');

    if (!userId) {
      toast.error('User ID not found. Please login again.');
      return;
    }

    // Check if location is selected
    if (!formData.locationId && formData.locationName !== 'Work From Home') {
      toast.error('Please select a work location or Work From Home');
      return;
    }

    // Set checkingIn to true immediately to prevent duplicate submissions
    setCheckingIn(true);

    const isWorkFromHome = formData.locationName === 'Work From Home';
    let selectedLocation = null;
    let locationName = 'Work From Home';
    let actualGPSLocation = null; // Declare outside to use in payload
    let userIPAddress = null; // Declare outside to use in payload
    let ipMatchesOffice = false; // Declare outside for logging

    // If not Work From Home, validate location
    if (!isWorkFromHome) {
      // Find selected location
      selectedLocation = workLocations.find(loc => loc.id === formData.locationId);
      
      if (!selectedLocation) {
        toast.error('Selected location not found');
        return;
      }

      locationName = selectedLocation.locationName;

      // STEP 1: Check IP Address First (Priority)
      // If IP matches office network → Allow check-in immediately (skip GPS check)
      // If IP doesn't match → Proceed to Step 2 (GPS validation)
      
      // Get user's IP address for office network validation
      try {
        userIPAddress = await getUserIPAddress();
        console.log('User IP Address:', userIPAddress);
        
        // Check if IP matches office IP range (if configured in work location)
        if (userIPAddress && selectedLocation.ipRanges && selectedLocation.ipRanges.length > 0) {
          ipMatchesOffice = isIPInOfficeRange(userIPAddress, selectedLocation.ipRanges);
          console.log('IP Validation Result:', ipMatchesOffice ? '✅ MATCHES' : '❌ DOES NOT MATCH');
          if (ipMatchesOffice) {
            console.log('✅ IP address matches office network - check-in will be allowed immediately without GPS check');
            setIpValidationStatus('matched'); // Update UI to show IP validation passed
          } else {
            console.log('❌ IP address does not match - will proceed to GPS validation');
            setIpValidationStatus('failed'); // Update UI to show GPS is needed
          }
        } else {
          console.log('⚠️ IP validation skipped - IP ranges not configured in database');
          setIpValidationStatus(null); // IP validation not available
        }
      } catch (error) {
        console.warn('Failed to get IP address:', error);
      }
      
      // STEP 2: Fetch GPS Location (Only if IP doesn't match)
      // If IP matches, skip GPS fetch for faster check-in
      // If IP doesn't match, fetch GPS as fallback for backend validation
      
      const locationNameLower = selectedLocation.locationName.toLowerCase();
      const isHeadOffice = locationNameLower.includes('head office') || locationNameLower.includes('headoffice');
      
      if (!ipMatchesOffice) {
        // IP doesn't match - need GPS for validation
        console.log('Fetching GPS location for validation...');
        
        if (isHeadOffice) {
          // For Head Office, always fetch fresh GPS location
          try {
            actualGPSLocation = await getUserLocation(true);
            setUserLocation(actualGPSLocation);
          } catch (error) {
            // If GPS fetch fails, check if we have existing location
            if (userLocation && !userLocation.isManual) {
              actualGPSLocation = userLocation;
            } else {
              toast.error('Unable to get your location. Please enable location services and try again.');
              setCheckingIn(false);
              return;
            }
          }
        } else {
          // For non-Head Office locations, use existing userLocation if available
          actualGPSLocation = userLocation;
          
          // Get actual GPS location if not available
          if (!actualGPSLocation) {
            try {
              actualGPSLocation = await getUserLocation(true);
            } catch (error) {
              toast.error('Unable to get your location. Please enable location services and try again.');
              setCheckingIn(false);
              return;
            }
          }
        }
        
        console.log('GPS location fetched for backend validation');
      } else {
        // IP matches - use existing location if available, but don't wait for GPS
        console.log('IP matches - using existing location data (if available) without waiting for GPS');
        actualGPSLocation = userLocation; // Use existing location, but don't block on GPS fetch
      }
      
      console.log('Location data prepared for backend validation:');
      console.log('- IP Matches Office:', ipMatchesOffice ? '✅ YES (GPS check skipped)' : '❌ NO (GPS check required)');
      console.log('- User Location:', actualGPSLocation ? {
        latitude: actualGPSLocation.latitude,
        longitude: actualGPSLocation.longitude,
        accuracy: actualGPSLocation.accuracy
      } : 'Not available (will be validated by backend)');
      console.log('- Selected Location:', {
        id: selectedLocation.id,
        name: selectedLocation.locationName,
        hasIPRanges: !!selectedLocation.ipRanges,
        ipRangesCount: selectedLocation.ipRanges ? selectedLocation.ipRanges.length : 0
      });
    }

    // Proceed with check-in
    try {
      // IP address already fetched above (if not Work From Home)
      // If Work From Home, try to get IP address now (optional, backend will extract from headers)
      if (isWorkFromHome && !userIPAddress) {
        try {
          userIPAddress = await getUserIPAddress();
          console.log('User IP Address (Work From Home):', userIPAddress);
        } catch (error) {
          console.warn('Failed to get IP address (backend will extract from headers):', error);
        }
      }
      
      console.log('Sending check-in request to backend with IP:', userIPAddress || 'will be extracted from headers');

      // Prepare payload according to API documentation - include all required and optional parameters
      const payload = {
        action: 'check-in',
        // Required parameters
        userId: userId,
        date: getCurrentDate(), // Format: DD-MM-YYYY
        location: locationName,
        checkInTime: getCurrentDateTime(), // Format: DD/MM/YYYY HH:MM AM/PM
        // Optional parameters - explicitly include all even if null/empty
        notes: formData.notes.trim() || '',
        latitude: !isWorkFromHome && actualGPSLocation ? actualGPSLocation.latitude : null,
        longitude: !isWorkFromHome && actualGPSLocation ? actualGPSLocation.longitude : null,
        locationAccuracy: !isWorkFromHome && actualGPSLocation ? actualGPSLocation.accuracy : null,
        userIPAddress: userIPAddress || null,
        locationId: !isWorkFromHome ? formData.locationId : null
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      // Handle response according to API documentation
      if (result?.success) {
        toast.success(result?.message || 'Checked-in successfully');
        setShowCheckInModal(false);
        setFormData(prev => ({ ...prev, locationId: '', locationName: '', notes: '' }));
        setConfirmAtOffice(false);
        setIpValidationStatus(null); // Reset IP validation status
        // Fetch updated attendance data
        await fetchTodayAttendance();
      } else {
        // Handle specific error types from API
        const errorMessage = result?.message || 'Failed to check in';
        const errorType = result?.error;
        const errorData = result?.data;

        // Show detailed error message based on error type
        if (errorType === 'LOCATION_VALIDATION_FAILED' && errorData) {
          // IP validation failed and GPS check also failed
          const distance = Math.round(errorData.distance || 0);
          const accuracy = errorData.accuracy || null;
          const worstCaseDistance = Math.round(errorData.worstCaseDistance || 0);
          const allowedRadius = errorData.allowedRadius || 50;
          let detailedMsg = `${errorMessage}\n\nDistance: ${distance}m, Allowed: ${allowedRadius}m`;
          if (accuracy) {
            detailedMsg += `\nGPS Accuracy: ±${accuracy}m`;
            detailedMsg += `\nWorst Case Distance: ${worstCaseDistance}m`;
          }
          toast.error(detailedMsg);
        } else if (errorType === 'IP_VALIDATION_FAILED') {
          // IP doesn't match and no GPS coordinates provided
          toast.error(errorMessage);
        } else if (errorType === 'LOCATION_ACCURACY_TOO_HIGH') {
          // GPS accuracy is too poor
          toast.error(errorMessage);
        } else if (errorType === 'WFH_NOT_ALLOWED' && errorData) {
          // Work From Home not allowed (user is inside office)
          const distance = Math.round(errorData.distance || 0);
          const office = errorData.office || 'office';
          toast.error(`${errorMessage}\n\nYou are ${distance}m inside ${office}.`);
        } else {
          toast.error(errorMessage);
        }
      }
    } catch (error) {
      console.error('Error checking in:', error);
      
      // Handle HTTP errors
      if (error.response) {
        const errorData = error.response.data;
        const errorMessage = errorData?.message || 'Failed to check in';
        const errorType = errorData?.error;
        const errorDataPayload = errorData?.data;
        
        // Handle specific error types
        if (errorType === 'LOCATION_VALIDATION_FAILED' && errorDataPayload) {
          // IP validation failed and GPS check also failed
          const distance = Math.round(errorDataPayload.distance || 0);
          const accuracy = errorDataPayload.accuracy || null;
          const worstCaseDistance = Math.round(errorDataPayload.worstCaseDistance || 0);
          const allowedRadius = errorDataPayload.allowedRadius || 50;
          let detailedMsg = `${errorMessage}\n\nDistance: ${distance}m, Allowed: ${allowedRadius}m`;
          if (accuracy) {
            detailedMsg += `\nGPS Accuracy: ±${accuracy}m`;
            detailedMsg += `\nWorst Case Distance: ${worstCaseDistance}m`;
          }
          toast.error(detailedMsg);
        } else if (errorType === 'IP_VALIDATION_FAILED') {
          // IP doesn't match and no GPS coordinates provided
          toast.error(errorMessage);
        } else if (errorType === 'LOCATION_ACCURACY_TOO_HIGH') {
          // GPS accuracy is too poor
          toast.error(errorMessage);
        } else if (errorType === 'WFH_NOT_ALLOWED' && errorDataPayload) {
          // Work From Home not allowed (user is inside office)
          const distance = Math.round(errorDataPayload.distance || 0);
          const office = errorDataPayload.office || 'office';
          toast.error(`${errorMessage}\n\nYou are ${distance}m inside ${office}.`);
        } else {
          toast.error(errorMessage);
        }
      } else if (error.request) {
        // Network error
        toast.error('Network error. Please check your internet connection and try again.');
      } else {
        // Other errors
        const errorMessage = error.message || 'Something went wrong. Please try again.';
        toast.error(errorMessage);
      }
    } finally {
      setCheckingIn(false);
    }
  };

  const handleCheckOut = async () => {
    const userId = localStorage.getItem('id');

    if (!userId) {
      toast.error('User ID not found. Please login again.');
      return;
    }

    setCheckingOut(true);
    try {
      // Prepare payload according to API documentation - include all required and optional parameters
      const payload = {
        action: 'check-out',
        // Required parameters
        userId: userId,
        date: getCurrentDate(), // Format: DD-MM-YYYY
        // Optional parameters - explicitly include even if not provided (API will use current UTC time)
        checkOutTime: getCurrentDateTime() // Format: DD/MM/YYYY HH:MM AM/PM or HH:MM:SS
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result?.message || 'Checked-out successfully');
        // Fetch updated attendance data
        await fetchTodayAttendance();
      } else {
        toast.error(result?.message || 'Failed to check out');
      }
    } catch (error) {
      console.error('Error checking out:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setCheckingOut(false);
    }
  };

  // Check if user can check in (considering Travel leave)
  const canCheckIn = () => {
    if (!attendanceData) return true; // No attendance record, can check in
    
    // If already checked out and it's not Travel leave, can't check in again
    if (attendanceData.checkInTime && attendanceData.checkOutTime && attendanceData.leaveType !== 'Travel') {
      return false;
    }
    
    // If Travel leave, check if current time is after Travel check-out time
    if (attendanceData.leaveType === 'Travel' && rawAttendanceData && rawAttendanceData.checkOutTime) {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentTimeInMinutes = currentHour * 60 + currentMinute;
      
      // Parse Travel check-out time (format: "HH:MM" or "HH:MM:SS")
      const travelCheckOutTime = rawAttendanceData.checkOutTime;
      const [outHour, outMinute] = travelCheckOutTime.split(':').map(Number);
      const travelCheckOutTimeInMinutes = outHour * 60 + outMinute;
      
      // Can check in if current time is after Travel check-out time
      return currentTimeInMinutes >= travelCheckOutTimeInMinutes;
    }
    
    // If checked in but not checked out, can't check in again
    if (attendanceData.checkInTime && !attendanceData.checkOutTime) {
      return false;
    }
    
    return true;
  };

  const isCheckedIn = attendanceData && attendanceData.checkInTime && !attendanceData.checkOutTime;
  const isCheckedOut = attendanceData && attendanceData.checkInTime && attendanceData.checkOutTime && attendanceData.leaveType !== 'Travel';
  const isTravelLeaveActive = attendanceData && attendanceData.leaveType === 'Travel' && rawAttendanceData && rawAttendanceData.checkOutTime && (() => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeInMinutes = currentHour * 60 + currentMinute;
    
    const travelCheckOutTime = rawAttendanceData.checkOutTime;
    const [outHour, outMinute] = travelCheckOutTime.split(':').map(Number);
    const travelCheckOutTimeInMinutes = outHour * 60 + outMinute;
    
    return currentTimeInMinutes < travelCheckOutTimeInMinutes;
  })();

  return (
    <div className="employee-form">
      {/* Confetti Animation Overlay */}
      {showConfetti && confettiData && (
        <div 
          style={{
            position: 'fixed',
            top: -200,
            right: -50,
            width: '50%',
            height: '100%',
            zIndex: 9999,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            overflow: 'hidden'
          }}
        >
          <Lottie
            animationData={confettiData}
            loop={false}
            autoplay={true}
            style={{ width: '100%', height: '100%', maxWidth: '600px' }}
            onComplete={() => {
              console.log('Confetti animation completed');
            }}
            onLoopComplete={() => {
              console.log('Confetti animation loop completed');
            }}
          />
        </div>
      )}
      {/* Debug info - remove in production */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{ position: 'fixed', bottom: 10, right: 10, background: 'rgba(0,0,0,0.7)', color: 'white', padding: '10px', fontSize: '12px', zIndex: 10000 }}>
          <div>showConfetti: {showConfetti ? 'true' : 'false'}</div>
          <div>confettiData: {confettiData ? 'loaded' : 'null'}</div>
          <div>birthdayEmployees: {birthdayEmployees.length}</div>
          <div>shouldShow: {shouldShowConfettiToday() ? 'true' : 'false'}</div>
        </div>
      )}
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title */}
          <div className="row mb-3 employee-header-row">
            <div className="col-md-12">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h1 className="employee-title">
                    Dashboard
                    <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
                  </h1>
                  <p className="employee-description">
                    Check in and check out for attendance tracking.
                  </p>
                </div>
                {birthdayEmployees && birthdayEmployees.length > 0 && (
                  <div className="dashboard-birthday-dropdown-container" style={{ position: 'relative' }}>
                    <div className="dashboard-birthday-header">
                      {loadingBirthdayEmployees ? (
                        <Loading size={20} />
                      ) : (
                        <div className="dashboard-birthday-header-text">
                          <i className="fas fa-birthday-cake mr-2"></i>
                          <span>Today's Birthday: </span>
                          <span className="dashboard-birthday-header-names">
                            {birthdayEmployees[0].firstName}
                            {birthdayEmployees.length > 1 && (
                              <span 
                                className="dashboard-birthday-more-count"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowBirthdayDropdown(!showBirthdayDropdown);
                                }}
                                style={{ cursor: 'pointer', marginLeft: '4px' }}
                              >
                                +{birthdayEmployees.length - 1}
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                    {showBirthdayDropdown && birthdayEmployees.length > 1 && (
                      <div className="dropdown-menu dropdown-menu-right dashboard-birthday-dropdown" style={{ 
                        display: 'block',
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '8px',
                        minWidth: '200px',
                        zIndex: 1000
                      }}>
                        <div className="dropdown-header" style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                          <i className="fas fa-birthday-cake mr-2"></i>
                          Today's Birthdays ({birthdayEmployees.length})
                        </div>
                        <div className="dropdown-divider"></div>
                        {birthdayEmployees.map((employee, index) => (
                          <div key={index} className="dropdown-item" style={{ padding: '8px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <i className="fas fa-user-circle mr-2" style={{ color: '#0094a9' }}></i>
                              <div>
                                <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                  {employee.firstName} {employee.middleName} {employee.lastName}
                                </div>
                                {employee.designation && (
                                  <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '2px' }}>
                                    {employee.designation}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Check-in/Check-out Section - 3 Column Layout */}
          <div className="row mb-4">
            {/* Column 1: Check In/Out */}
            <div className="col-12 col-md-4 mb-4">
              <div className="card employee-content-card dashboard-card">
                <div className="card-body dashboard-card-body">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3 className="employee-title dashboard-title">
                      <i className="fas fa-clock mr-2 dashboard-title-icon"></i>
                      Attendance
                    </h3>
                  </div>

                  {loading ? (
                    <Loading size={80} />
                  ) : (
                    <>
                      {/* Action Buttons */}
                      <div className="d-flex flex-column dashboard-action-buttons">
                        {canCheckIn() && (
                          <button
                            type="button"
                            className="btn btn-primary dashboard-btn-checkin"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, locationId: '', locationName: '', notes: '' }));
                              setConfirmAtOffice(false);
                              setShowCheckInModal(true);
                            }}
                          >
                            <i className="fas fa-sign-in-alt mr-2"></i>
                            {attendanceData && attendanceData.leaveType === 'Travel' && rawAttendanceData && rawAttendanceData.checkOutTime
                              ? 'Check In (After Travel)'
                              : 'Check In'}
                          </button>
                        )}

                        {isCheckedIn && !isTravelLeaveActive && (
                          <button
                            type="button"
                            className="btn btn-success dashboard-btn-checkout"
                            onClick={handleCheckOut}
                            disabled={checkingOut}
                          >
                            {checkingOut ? (
                              <>
                                <span className="spinner-border spinner-border-sm mr-2"></span>
                                Checking Out...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-sign-out-alt mr-2"></i>
                                Check Out
                              </>
                            )}
                          </button>
                        )}

                        {isTravelLeaveActive && (
                          <div className="alert alert-warning dashboard-alert-checked-out" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107', color: '#856404' }}>
                            <i className="fas fa-plane mr-2 dashboard-alert-icon"></i>
                            <div>
                              <strong className="dashboard-alert-text">
                                Travel Period Active
                              </strong>
                              <div style={{ fontSize: '12px', marginTop: '5px' }}>
                                Travel period: {rawAttendanceData.checkInTime} - {rawAttendanceData.checkOutTime}
                                <br />
                                Check in will be available after {rawAttendanceData.checkOutTime}
                              </div>
                            </div>
                          </div>
                        )}

                        {isCheckedOut && !isTravelLeaveActive && (
                          <div className="alert alert-info dashboard-alert-checked-out">
                            <i className="fas fa-check-circle mr-2 dashboard-alert-icon"></i>
                            <strong className="dashboard-alert-text">
                              You have already checked out for today.
                            </strong>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Column 2: Leave Balance */}
            <div className="col-12 col-md-8 mb-4">
              <div className="card employee-content-card dashboard-card">
                <div className="card-body dashboard-card-body dashboard-leave-balance-container">
                  <div className="d-flex justify-content-between align-items-center dashboard-leave-balance-header">
                    <h3 className="employee-title dashboard-title">
                      <i className="fas fa-calendar-alt mr-2 dashboard-title-icon"></i>
                      Leave Balance
                    </h3>
                  </div>

                  {loadingLeaveBalance ? (
                    <div className="text-center dashboard-leave-balance-loading">
                      <Loading size={80} />
                    </div>
                  ) : leaveBalance ? (
                    <div className="row leave-balance-row dashboard-leave-balance-row">
                      <div className="col-md-3 col-sm-6 dashboard-leave-balance-col">
                        <div className="card employee-content-card leave-balance-card-wrapper dashboard-leave-balance-card-wrapper">
                          <div className="card-body leave-balance-card-body dashboard-leave-balance-card-body">
                            <div className="dashboard-leave-balance-header-row">
                              <div className="leave-balance-icon leave-balance-icon-casual dashboard-leave-balance-icon">
                                <i className="fas fa-calendar-check"></i>
                              </div>
                              <h3 className="leave-balance-title dashboard-leave-balance-title">
                                Casual Leave
                              </h3>
                            </div>
                            <div className="dashboard-leave-balance-content">
                              <div className="leave-balance-value dashboard-leave-balance-value dashboard-leave-balance-value-casual">
                                {leaveBalance.casual?.balance || 0}
                              </div>
                              <div className="leave-balance-label dashboard-leave-balance-label">
                                Available
                              </div>
                              <div className="leave-balance-details">
                                <div className="leave-balance-detail-row">
                                  <span>Allocated:</span>
                                  <span className="leave-balance-detail-value">{leaveBalance.casual?.allocated || 0}</span>
                                </div>
                                <div className="leave-balance-detail-row">
                                  <span>Used:</span>
                                  <span className="leave-balance-detail-value">{leaveBalance.casual?.used || 0}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-3 col-sm-6 dashboard-leave-balance-col">
                        <div className="card employee-content-card leave-balance-card-wrapper dashboard-leave-balance-card-wrapper">
                          <div className="card-body leave-balance-card-body dashboard-leave-balance-card-body">
                            <div className="dashboard-leave-balance-header-row">
                              <div className="leave-balance-icon leave-balance-icon-sick dashboard-leave-balance-icon">
                                <i className="fas fa-heartbeat"></i>
                              </div>
                              <h3 className="leave-balance-title dashboard-leave-balance-title">
                                Sick Leave
                              </h3>
                            </div>
                            <div className="dashboard-leave-balance-content">
                              <div className="leave-balance-value dashboard-leave-balance-value dashboard-leave-balance-value-sick">
                                {leaveBalance.sick?.balance || 0}
                              </div>
                              <div className="leave-balance-label dashboard-leave-balance-label">
                                Available
                              </div>
                              <div className="leave-balance-details ">
                                <div className="leave-balance-detail-row">
                                  <span>Allocated:</span>
                                  <span className="leave-balance-detail-value">{leaveBalance.sick?.allocated || 0}</span>
                                </div>
                                <div className="leave-balance-detail-row">
                                  <span>Used:</span>
                                  <span className="leave-balance-detail-value">{leaveBalance.sick?.used || 0}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-3 col-sm-6 dashboard-leave-balance-col">
                        <div className="card employee-content-card leave-balance-card-wrapper dashboard-leave-balance-card-wrapper">
                          <div className="card-body leave-balance-card-body dashboard-leave-balance-card-body">
                            <div className="dashboard-leave-balance-header-row">
                              <div className="leave-balance-icon leave-balance-icon-earned dashboard-leave-balance-icon">
                                <i className="fas fa-star"></i>
                              </div>
                              <h3 className="leave-balance-title dashboard-leave-balance-title">
                                Earned Leave
                              </h3>
                            </div>
                            <div className="dashboard-leave-balance-content">
                              <div className="leave-balance-value dashboard-leave-balance-value dashboard-leave-balance-value-earned">
                                {leaveBalance.earned?.balance || 0}
                              </div>
                              <div className="leave-balance-label dashboard-leave-balance-label">
                                Available
                              </div>
                              <div className="leave-balance-details">
                                <div className="leave-balance-detail-row">
                                  <span>Allocated:</span>
                                  <span className="leave-balance-detail-value">{leaveBalance.earned?.allocated || 0}</span>
                                </div>
                                <div className="leave-balance-detail-row">
                                  <span>Used:</span>
                                  <span className="leave-balance-detail-value">{leaveBalance.earned?.used || 0}</span>
                                </div>
                                {leaveBalance.earned?.carryForward !== undefined && leaveBalance.earned?.carryForward !== null && (
                                  <div className="leave-balance-detail-row">
                                    <span>Carry Forward:</span>
                                    <span className="leave-balance-detail-value leave-balance-detail-value-earned">{leaveBalance.earned.carryForward}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-3 col-sm-6 dashboard-leave-balance-col">
                        <div className="card employee-content-card leave-balance-card-wrapper dashboard-leave-balance-card-wrapper">
                          <div className="card-body leave-balance-card-body dashboard-leave-balance-card-body">
                            <div className="dashboard-leave-balance-header-row">
                              <div className="leave-balance-icon leave-balance-icon-lop dashboard-leave-balance-icon">
                                <i className="fas fa-exclamation-triangle"></i>
                              </div>
                              <h3 className="leave-balance-title dashboard-leave-balance-title">
                                LOP
                              </h3>
                            </div>
                            <div className="dashboard-leave-balance-content">
                              <div className="leave-balance-value dashboard-leave-balance-value dashboard-leave-balance-value-lop">
                                {leaveBalance.lop?.used || 0}
                              </div>
                              <div className="leave-balance-label dashboard-leave-balance-label">
                                Days Taken
                              </div>
                                <div className="leave-balance-details">
                                <div className="leave-balance-detail-row">
                                  <span>No limit</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="dashboard-leave-balance-error">No leave balance data available</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Today's Attendance Display - Full Width */}
          {attendanceData && (
            <div className="row mb-4">
              <div className="col-12">
                <div className="card dashboard-attendance-card">
                  <div className="card-body dashboard-attendance-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="dashboard-attendance-header">
                        <i className="fas fa-calendar-check mr-2 dashboard-attendance-header-icon"></i>
                        Today's Attendance
                      </h5>
                      <span className={`badge ${attendanceData.status === 'present' ? 'badge-success' : 'badge-warning'} dashboard-badge`}>
                        {attendanceData.status?.toUpperCase() || 'PRESENT'}
                      </span>
                    </div>
                    <div className="row dashboard-attendance-row">
                      {/* Row 1: Date | Location */}
                      <div className="col-md-6 mb-3">
                        <div className="dashboard-info-box">
                          <div className="dashboard-info-label">
                            Date
                          </div>
                          <div className="dashboard-info-value">
                            <i className="fas fa-calendar mr-2 dashboard-info-icon"></i>
                            {formatDate(attendanceData.date)}
                          </div>
                        </div>
                      </div>
                      {attendanceData.location && (
                        <div className="col-md-6 mb-3">
                          <div className="dashboard-info-box-location">
                            <div className="dashboard-info-label">
                              Location
                            </div>
                            <div className="dashboard-info-value">
                              <i className="fas fa-map-marker-alt mr-2 dashboard-info-icon-location"></i>
                              {attendanceData.location}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Row 2: Check-in Time | Check-out Time */}
                      {attendanceData.checkInTime && (
                        <div className="col-md-6 mb-3">
                          <div className="dashboard-info-box-checkin">
                            <div className="dashboard-info-label">
                              Check-in Time
                            </div>
                            <div className="dashboard-info-value">
                              <i className="fas fa-sign-in-alt mr-2 dashboard-info-icon-checkin"></i>
                              {attendanceData.checkInTime || 'N/A'}
                            </div>
                          </div>
                        </div>
                      )}
                      {attendanceData.checkOutTime && (
                        <div className="col-md-6 mb-3">
                          <div className="dashboard-info-box-checkout">
                            <div className="dashboard-info-label">
                              Check-out Time
                            </div>
                            <div className="dashboard-info-value">
                              <i className="fas fa-sign-out-alt mr-2 dashboard-info-icon-checkout"></i>
                              {attendanceData.checkOutTime || 'N/A'}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Row 3: Notes | Total Hours */}
                      {attendanceData.notes && (
                        <div className="col-md-6 mb-3">
                          <div className="dashboard-info-box-notes">
                            <div className="dashboard-info-label">
                              Notes
                            </div>
                            <div className="dashboard-info-value-small">
                              <i className="fas fa-sticky-note mr-2 dashboard-info-icon-notes"></i>
                              {attendanceData.notes}
                            </div>
                          </div>
                        </div>
                      )}
                      {attendanceData.totalHours && (
                        <div className="col-md-6 mb-3">
                          <div className="dashboard-info-box-hours">
                            <div className="dashboard-info-label">
                              Total Hours
                            </div>
                            <div className="dashboard-info-value">
                              <i className="fas fa-clock mr-2 dashboard-info-icon-hours"></i>
                              {attendanceData.totalHours}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </section>

      {/* Check-in Modal - Simplified (Notes Only) */}
      {showCheckInModal && (
        <div className="modal-overlay" onClick={() => {
          setShowCheckInModal(false);
          setFormData(prev => ({ ...prev, locationId: '', locationName: '', notes: '' }));
          setConfirmAtOffice(false);
          setIpValidationStatus(null); // Reset IP validation status
        }}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Check In</h3>
              <button className="modal-close" onClick={() => {
                setShowCheckInModal(false);
                setFormData(prev => ({ ...prev, locationId: '', locationName: '', notes: '' }));
                setConfirmAtOffice(false);
                setIpValidationStatus(null); // Reset IP validation status
              }}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="locationId" className="form-label">
                  Location <span className="text-danger">*</span>
                </label>
                <select
                  id="locationId"
                  className="form-input dashboard-modal-select"
                  value={formData.locationName === 'Work From Home' ? 'work-from-home' : formData.locationId}
                  onChange={async (e) => {
                    const selectedValue = e.target.value;
                    if (selectedValue === 'work-from-home') {
                      setFormData(prev => ({ 
                        ...prev, 
                        locationId: '', 
                        locationName: 'Work From Home' 
                      }));
                      setConfirmAtOffice(false); // Reset confirmation for Work From Home
                      setIpValidationStatus(null); // Reset IP validation status
                    } else {
                      const selectedLoc = workLocations.find(loc => loc.id === selectedValue);
                      const locationName = selectedLoc ? selectedLoc.locationName : '';
                      const isHeadOffice = locationName.toLowerCase().includes('head office') || locationName.toLowerCase().includes('headoffice');
                      
                      setFormData(prev => ({ 
                        ...prev, 
                        locationId: selectedValue,
                        locationName: locationName
                      }));
                      setConfirmAtOffice(false);
                      setIpValidationStatus(null); // Reset IP validation status when location changes
                      
                      // STEP 1: Check IP Address First (Immediate validation)
                      // If IP matches → Skip GPS fetch for instant check-in
                      // If IP doesn't match → Fetch GPS as fallback
                      let userIPAddress = null;
                      let ipMatchesOffice = false;
                      
                      try {
                        userIPAddress = await getUserIPAddress();
                        console.log('Location selected - Checking IP address:', userIPAddress);
                        
                        // Check if IP matches office IP range
                        if (userIPAddress && selectedLoc && selectedLoc.ipRanges && selectedLoc.ipRanges.length > 0) {
                          ipMatchesOffice = isIPInOfficeRange(userIPAddress, selectedLoc.ipRanges);
                          console.log('IP Validation Result:', ipMatchesOffice ? '✅ MATCHES - GPS not needed' : '❌ DOES NOT MATCH - GPS required');
                          
                          if (ipMatchesOffice) {
                            setIpValidationStatus('matched');
                            console.log('✅ IP address matches - Check-in will be instant, no GPS needed');
                            // IP matches - don't fetch GPS, use existing location if available
                            // This allows immediate check-in without waiting
                            return; // Exit early - no GPS fetch needed
                          } else {
                            setIpValidationStatus('failed');
                            console.log('❌ IP address does not match - Fetching GPS location...');
                          }
                        } else {
                          console.log('⚠️ IP validation not available - Fetching GPS location...');
                          setIpValidationStatus(null);
                        }
                      } catch (error) {
                        console.warn('Failed to get IP address:', error);
                        setIpValidationStatus(null);
                      }
                      
                      // STEP 2: Fetch GPS Location (Only if IP doesn't match)
                      // Only fetch GPS if IP validation failed or is not available
                      if (!ipMatchesOffice && selectedLoc) {
                        if (isHeadOffice) {
                          // For Head Office, fetch actual GPS location
                          getUserLocation(true).then(location => {
                            setUserLocation(location);
                          }).catch(error => {
                            console.warn('Failed to fetch GPS location for Head Office:', error);
                            // Keep existing location if fetch fails
                          });
                        } else {
                          // For other locations, try to get existing location or fetch if not available
                          if (!userLocation) {
                            getUserLocation(true).then(location => {
                              setUserLocation(location);
                            }).catch(error => {
                              console.warn('Failed to fetch GPS location:', error);
                            });
                          }
                        }
                      }
                    }
                  }}
                  required
                >
                  <option value="">Select Location</option>
                  <option value="work-from-home">Work From Home</option>
                  {workLocations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.locationName}
                    </option>
                  ))}
                </select>
                {workLocations.length === 0 && (
                  <small className="text-muted dashboard-text-muted-small">
                    No work locations available
                  </small>
                )}
              </div>

              {formData.locationName && formData.locationName !== 'Work From Home' && (
                <>
                  {userLocation && (
                    <div className={`alert ${userLocation.accuracy > 2000 ? 'alert-warning' : userLocation.accuracy > 100 ? 'alert-info' : 'alert-success'} dashboard-location-alert`}>
                      <i className={`fas ${userLocation.accuracy > 2000 ? 'fa-exclamation-triangle' : 'fa-map-marker-alt'} mr-2`}></i>
                      <strong>Your Location:</strong> {userLocation.latitude.toFixed(6)}, {userLocation.longitude.toFixed(6)}
                      {!userLocation.isManual && (
                        <>
                          <span className="dashboard-location-info-block">
                            Accuracy: ±{Math.round(userLocation.accuracy)} meters
                          </span>
                          {isDesktop && (
                            <span className="dashboard-location-info-block-small">
                              {userLocation.accuracy > 2000 ? (
                                <span className="dashboard-location-warning-text">
                                  ⚠️ Using IP-based location (poor accuracy). If connected to office Wi-Fi, location may improve with time.
                                </span>
                              ) : userLocation.accuracy > 100 ? (
                                <span className="dashboard-location-info-text">
                                  ℹ️ Wi-Fi positioning accuracy is moderate. Ensure you're connected to office Wi-Fi.
                                </span>
                              ) : (
                                <span className="dashboard-location-success-text">
                                  ✓ Good Wi-Fi positioning accuracy
                                </span>
                              )}
                            </span>
                          )}
                        </>
                      )}
                      {userLocation.isManual && (
                        <span className="dashboard-location-info-block-manual">
                          ✓ Using work location coordinates (you confirmed you're at the office)
                        </span>
                      )}
                    </div>
                  )}
                  {/* Show IP validation success message when IP matches */}
                  {ipValidationStatus === 'matched' && (
                    <div className="alert alert-success dashboard-fetching-alert" style={{ backgroundColor: '#d4edda', borderColor: '#c3e6cb', color: '#155724' }}>
                      <i className="fas fa-check-circle mr-2"></i>
                      <small>
                        ✅ IP address verified - You're connected to office network. Check-in will proceed immediately without GPS validation.
                      </small>
                    </div>
                  )}
                  
                  {/* Show GPS fetching message only when IP doesn't match or IP validation is not available */}
                  {fetchingLocation && ipValidationStatus !== 'matched' && (
                    <div className="alert alert-info dashboard-fetching-alert">
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      <small>
                        {isDesktop ? (
                          <>Fetching location via Wi-Fi positioning... This may take up to 3 minutes for better accuracy. Please ensure you're connected to office Wi-Fi.</>
                        ) : (
                          <>Fetching your location via GPS... Please wait.</>
                        )}
                      </small>
                    </div>
                  )}
                  {!userLocation && !fetchingLocation && (
                    <div className="alert alert-warning dashboard-location-wait-alert">
                      <i className="fas fa-exclamation-triangle mr-2"></i>
                      <small>Location is being fetched. Please wait a moment...</small>
                    </div>
                  )}
                  
                  {/* IP Ranges Configuration Check */}
                  {formData.locationName && formData.locationName !== 'Work From Home' && (() => {
                    const selectedLoc = workLocations.find(loc => loc.id === formData.locationId);
                    const hasIPRanges = selectedLoc && selectedLoc.ipRanges && selectedLoc.ipRanges.length > 0;
                    const isEmpty = selectedLoc && selectedLoc.ipRanges && selectedLoc.ipRanges.length === 0;
                    
                    if (!hasIPRanges || isEmpty) {
                      return (
                        <div className="alert alert-warning dashboard-ip-warning-alert">
                          <i className="fas fa-exclamation-triangle mr-2 dashboard-ip-warning-icon"></i>
                          <strong className="dashboard-ip-warning-title">⚠️ IP Ranges Not Configured</strong>
                          <p className="dashboard-ip-warning-text">
                            Office network IP ranges are {isEmpty ? 'EMPTY' : 'NOT CONFIGURED'} in the database for this location. 
                            IP-based validation will not work until IP ranges are added.
                          </p>
                          <div className="dashboard-ip-warning-box">
                            <strong className="dashboard-ip-warning-title">To Fix:</strong>
                            <ol className="dashboard-ip-warning-list">
                              <li>Find your office network IP range (check router settings or network admin)</li>
                              <li>Update database with IP ranges for Location ID: <code className="dashboard-ip-warning-code">{selectedLoc?.id}</code></li>
                              <li>Example format: <code className="dashboard-ip-warning-code">["192.168.1.0/24", "10.0.0.0/8"]</code></li>
                            </ol>
                            <p className="dashboard-ip-warning-hint">
                              Check browser console (F12) for your current IP address and more details.
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Manual Confirmation for Office Wi-Fi */}
                  {(() => {
                    if (!isDesktop || !formData.locationName || formData.locationName === 'Work From Home') return null;
                    const locationNameLower = formData.locationName.toLowerCase();
                    const isHeadOffice = locationNameLower.includes('head office') || locationNameLower.includes('headoffice');
                    const showConfirmation = isHeadOffice || (userLocation && userLocation.accuracy > 2000);
                    if (!showConfirmation) return null;
                    
                    // return (
                    //   <div className="alert alert-info" style={{ marginBottom: '15px', padding: '12px', fontSize: '13px', backgroundColor: '#e7f3ff', border: '1px solid #b3d9ff' }}>
                    //     <div style={{ marginBottom: '10px' }}>
                    //       <strong style={{ color: '#004085' }}>📍 Connected to Office Wi-Fi?</strong>
                    //       <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#004085' }}>
                    //         {isHeadOffice 
                    //           ? 'Head Office selected. The checkbox is automatically checked to use office location coordinates for accurate check-in.'
                    //           : 'Browsers cannot directly detect Wi-Fi networks, but if you\'re connected to office Wi-Fi and the location accuracy is poor, you can confirm your location manually.'}
                    //       </p>
                    //       {hasIPRanges && (
                    //         <p style={{ margin: '5px 0 0 0', fontSize: '11px', color: '#155724', backgroundColor: '#d4edda', padding: '5px', borderRadius: '4px' }}>
                    //           ✅ IP ranges configured: {selectedLoc.ipRanges.length} range(s) - IP validation enabled
                    //         </p>
                    //       )}
                    //     </div>
                    //   <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0 }}>
                    //     <input
                    //       type="checkbox"
                    //       checked={confirmAtOffice}
                    //       onChange={(e) => {
                    //         setConfirmAtOffice(e.target.checked);
                    //         if (e.target.checked) {
                    //           // Update location to use work location coordinates
                    //           const selectedLoc = workLocations.find(loc => loc.id === formData.locationId);
                    //           if (selectedLoc) {
                    //             setUserLocation({
                    //               latitude: selectedLoc.latitude,
                    //               longitude: selectedLoc.longitude,
                    //               accuracy: 50,
                    //               source: 'manual-confirmation',
                    //               isManual: true
                    //             });
                    //             toast.info('Using work location coordinates. Make sure you are actually at the office.');
                    //           }
                    //         } else {
                    //           // Reset to original location
                    //           getUserLocation(false).then(loc => {
                    //             setUserLocation(loc);
                    //           }).catch(() => {
                    //             // Keep current location if fetch fails
                    //           });
                    //         }
                    //       }}
                    //       style={{ marginRight: '8px', cursor: 'pointer' }}
                    //     />
                    //     <span style={{ fontSize: '13px', color: '#004085' }}>
                    //       <strong>I confirm I am at the office</strong> and connected to office Wi-Fi. Use work location coordinates for check-in.
                    //     </span>
                    //   </label>
                    //   <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: '#6c757d', fontStyle: 'italic' }}>
                    //     Note: This will use the selected work location's coordinates instead of GPS location.
                    //   </p>
                    //   </div>
                    // );
                  })()}
                </>
              )}

              {formData.locationName === 'Work From Home' && (
                <div className="alert alert-success dashboard-wfh-alert">
                  <i className="fas fa-home mr-2"></i>
                  <strong>Work From Home:</strong> Location validation will be skipped for this check-in.
                </div>
              )}

              <div className="form-group dashboard-modal-form-group">
                <label htmlFor="notes" className="form-label">
                  Notes <span className="dashboard-modal-notes-label-optional">(optional)</span>
                </label>
                <input
                  type="text"
                  id="notes"
                  className="form-input dashboard-modal-input"
                  placeholder="Enter any notes about your check-in (optional)"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowCheckInModal(false);
                  setFormData(prev => ({ ...prev, locationId: '', locationName: '', notes: '' }));
                  setConfirmAtOffice(false);
                  setIpValidationStatus(null); // Reset IP validation status
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary dashboard-modal-btn-primary"
                onClick={handleCheckIn}
                disabled={
                  checkingIn || 
                  (!formData.locationId && formData.locationName !== 'Work From Home') || 
                  (formData.locationName && formData.locationName !== 'Work From Home' && !userLocation && ipValidationStatus !== 'matched')
                }
              >
                {checkingIn ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2"></span>
                    Checking In...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check mr-2"></i>
                    Submit
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;